import React, { useState } from 'react';
import moment from 'moment';
import Header from '../../components/common/global-top-bar';
import SwitchBar from '../../components/switch-bar';
import { PEER_NAVIGATION } from '../peer-listing/peer-constants';
import {
  ScheduleOverviewResponse,
  useScheduleOverview,
  useScheduleOverviewDownload,
} from '../../hooks/useScheduleReporting';
import { Spinner } from '../../components/common';
import { useDomains } from '../../hooks/useClients';

const ScheduleOverviewPage: React.FC = () => {
  const [domainId, setDomainId] = useState<number | undefined>(undefined);
  const { data, isLoading } = useScheduleOverview({ domainId });
  const { data: domains } = useDomains();
  const [showNames, setShowNames] = useState(false);
  const [highlightThreshold, setHighlightThreshold] = useState<number | undefined>(undefined);

  return (
    <div>
      <Header heading="Schedule Overview" />
      <SwitchBar heading={PEER_NAVIGATION} position={1} />
      <ScheduleFilters
        showNames={showNames}
        setShowNames={setShowNames}
        highlightThreshold={highlightThreshold}
        setHighlightThreshold={setHighlightThreshold}
        domains={domains}
        domainId={domainId}
        setDomainId={setDomainId}
      />
      {isLoading && <Spinner />}
      {data && (
        <ScheduleOverviewTable showNames={showNames} highlightThreshold={highlightThreshold} scheduleData={data} />
      )}
    </div>
  );
};

interface ScheduleFiltersProps {
  showNames: boolean;
  setShowNames: (show: boolean) => void;
  highlightThreshold: number | undefined;
  setHighlightThreshold: (threshold: number | undefined) => void;
  domains?: { id: number; name: string }[];
  domainId: number | undefined;
  setDomainId: (id: number | undefined) => void;
}

const ScheduleFilters: React.FC<ScheduleFiltersProps> = ({
  showNames,
  setShowNames,
  highlightThreshold,
  setHighlightThreshold,
  domains,
  domainId,
  setDomainId,
}) => {
  const { isFetching: isFetchingCsv, refetch: refetchCsv } = useScheduleOverviewDownload(false, { domainId });

  const handleThresholdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty string or convert to number
    setHighlightThreshold(value === '' ? undefined : Number(value));
  };

  const onDownloadCSV = () => {
    refetchCsv();
  };

  return (
    <div className="gray-border-line bg-gray-background-light w-full border-b px-4 py-2">
      <div className="flex w-full flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="whitespace-nowrap font-medium text-neutral-600">Domain:</label>
            <select
              value={domainId ?? ''}
              onChange={(e) => setDomainId(e.target.value ? parseInt(e.target.value, 10) : undefined)}
              className="w-56 rounded border bg-white px-3 py-2"
            >
              <option value="">All domains</option>
              {domains?.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="whitespace-nowrap font-medium text-neutral-600">Show:</label>
            <select
              value={showNames ? 'names' : 'count'}
              onChange={(e) => setShowNames(e.target.value === 'names')}
              className="w-48 rounded border bg-white px-3 py-2"
            >
              <option value="count">Listener Count</option>
              <option value="names">Listener Names</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="whitespace-nowrap font-medium text-neutral-600">Highlight rows with</label>
            <input
              type="number"
              value={highlightThreshold ?? ''}
              onChange={handleThresholdChange}
              className="w-20 rounded border bg-white px-3 py-2"
            />
            <span className="whitespace-nowrap text-neutral-600">listeners</span>
          </div>
        </div>

        <button
          onClick={onDownloadCSV}
          className="whitespace-nowrap rounded bg-violet-950 px-4 py-2 text-white transition-colors hover:bg-violet-900"
          disabled={isFetchingCsv}
        >
          {isFetchingCsv ? 'Downloading...' : 'Download CSV'}
        </button>
      </div>
    </div>
  );
};

interface ScheduleOverviewProps {
  showNames: boolean;
  highlightThreshold: number | undefined;
  scheduleData: ScheduleOverviewResponse;
}

const ScheduleOverviewTable: React.FC<ScheduleOverviewProps> = ({ showNames, highlightThreshold, scheduleData }) => {
  // Creates half-hour time slots for a day in CST timezone
  const timeSlots = Array.from({ length: 48 }, (_, i) => {
    const hour = Math.floor(i / 2);
    const minute = i % 2 === 0 ? '00' : '30';
    return moment().tz('America/Chicago').startOf('day').add(hour, 'hours').add(minute, 'minutes');
  });

  // Creates a list of dates for the current week starting from today in CST timezone
  const weekDates = Array.from({ length: 7 }, (_, i) => moment().tz('America/Chicago').add(i, 'days'));

  return (
    <div className="w-full overflow-x-auto p-4">
      <table className="min-w-[900px] border-collapse text-sm">
        <thead>
          <tr className="bg-gray-100">
            <th className="w-28 border bg-gray-100 p-2 text-left">Time (CST)</th>
            {weekDates.map((date) => (
              <th key={date.format('YYYY-MM-DD')} className="w-36 border p-2 text-left">
                {date.format('ddd, MMM D')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {timeSlots.map((timeSlot) => (
            <tr key={timeSlot.format('HH:mm')}>
              <td className="border bg-white p-2 font-medium">{timeSlot.format('h:mm A')}</td>
              {weekDates.map((date) => {
                /*
                 * Timezone handling:
                 * 1. We display all times in CST (America/Chicago) for consistency
                 * 2. The backend API returns data with UTC timestamps as keys
                 * 3. To match the backend data, we need to convert our CST display times to UTC
                 *
                 * This ensures that regardless of the user's browser timezone, the schedule
                 * will always display correctly in CST, and the data will be correctly mapped
                 * to the UTC timestamps provided by the backend.
                 */
                const cstDateTime = moment
                  .tz('America/Chicago')
                  .year(date.year())
                  .month(date.month())
                  .date(date.date())
                  .hours(timeSlot.hours())
                  .minutes(timeSlot.minutes())
                  .seconds(0);

                const fullUtcDateTime = cstDateTime.utc().format('YYYY-MM-DDTHH:mm:ss+00:00');

                const listeners = scheduleData.time_slots[fullUtcDateTime]?.listener_display_names || [];
                const cellContent = showNames ? listeners.join(', ') || '-' : listeners.length || '-';
                const shouldHighlight = highlightThreshold && listeners.length === highlightThreshold;

                return (
                  <td
                    key={`${timeSlot.format('HH:mm')}-${date.format('YYYY-MM-DD')}`}
                    className={`border p-2 align-top ${shouldHighlight ? 'bg-yellow-100' : ''}`}
                  >
                    {cellContent}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ScheduleOverviewPage;
